import styles from './negativeStockProductsFormModal.module.css'
import { BooleanField, Field, FieldType, FieldValue, Modal, ModalId, O, useUIState } from '../../../../../features/ui'
import { useEffect, useMemo, useState } from 'react'
import { normalize } from '../../../../../utils/typeUtils'
import { Product } from '../../../state/types'

type Props = {
    products: Product[]
    enabledCategoryIds: string[]
    onSubmit: (products: Product[]) => void
}

enum ProductStatus {
    SELECTABLE = 'selectable',
    BY_CATEGORY = 'byCategory',
    LOTED = 'loted'
}

const statusOrder: Record<ProductStatus, number> = {
    [ProductStatus.SELECTABLE]: 0,
    [ProductStatus.BY_CATEGORY]: 1,
    [ProductStatus.LOTED]: 2
}

const statusClassName: Record<ProductStatus, string> = {
    [ProductStatus.SELECTABLE]: styles.item,
    [ProductStatus.BY_CATEGORY]: `${styles.item} ${styles.enabledByCategory}`,
    [ProductStatus.LOTED]: `${styles.item} ${styles.notAllowed}`
}

// Selección múltiple de productos para habilitar la venta sin stock
export const NegativeStockProductsFormModal = (props: Props) => {
    const { products, enabledCategoryIds, onSubmit } = props

    const [searchText, setSearchText] = useState<O<string>>()
    const [selectedIds, setSelectedIds] = useState<string[]>([])
    const [key, setKey] = useState(0)

    const { openedModal } = useUIState()

    useEffect(() => {
        if (openedModal === ModalId.NEGATIVE_STOCK_PRODUCTS_FORM) {
            setSearchText(undefined)
            setSelectedIds([])
            setKey(prevKey => prevKey + 1)
        }
    }, [openedModal])

    // Seleccionable / ya habilitado por su categoría (tildado y fijo) / con partidas (no se puede habilitar)
    const getStatus = (product: Product): ProductStatus => {
        if (product.loted) return ProductStatus.LOTED
        if (enabledCategoryIds.includes(product.categoryId)) return ProductStatus.BY_CATEGORY
        return ProductStatus.SELECTABLE
    }

    const disabledReason = (product: Product): O<string> => {
        const status = getStatus(product)
        if (status === ProductStatus.LOTED) return 'con partidas, no se puede habilitar'
        if (status === ProductStatus.BY_CATEGORY) return 'ya habilitado por su categoría'
        return undefined
    }

    // Activos y todavía no habilitados individualmente: primero los seleccionables, después los habilitados por categoría y al final los con partidas
    const candidates = useMemo(() => products
        .filter(product => !product.archived && !product.allowNegativeStock)
        .sort((product_A, product_B) =>
            statusOrder[getStatus(product_A)] - statusOrder[getStatus(product_B)] ||
            product_A.name.localeCompare(product_B.name, 'es', { numeric: true, sensitivity: 'base' }))
    , [products, enabledCategoryIds])

    const visibleProducts = useMemo(() => {
        const text = searchText?.trim() ? normalize(searchText.trim()) : undefined
        return text ?
            candidates.filter(product => normalize(product.name).includes(text) || product.code.toString().includes(text)) :
            candidates
    }, [candidates, searchText])

    const selectableVisibleIds = visibleProducts.filter(product => !disabledReason(product)).map(product => product.id)
    const allVisibleSelected = selectableVisibleIds.length > 0 && selectableVisibleIds.every(id => selectedIds.includes(id))

    const onToggleProduct = (productId: string) => (value?: boolean) => {
        setSelectedIds(prevIds => value ? [...prevIds, productId] : prevIds.filter(id => id !== productId))
    }

    const onToggleAllVisible = (value?: boolean) => {
        setSelectedIds(prevIds => value ?
            Array.from(new Set([...prevIds, ...selectableVisibleIds])) :
            prevIds.filter(id => !selectableVisibleIds.includes(id)))
    }

    const handleSubmit = () => onSubmit(products.filter(product => selectedIds.includes(product.id)))

    return (
        <Modal
            modalId={ModalId.NEGATIVE_STOCK_PRODUCTS_FORM}
            title="Habilitar Venta Sin Stock"
            widthSize="m"
            submitLabel={`Habilitar (${selectedIds.length})`}
            disableSubmit={selectedIds.length === 0}
            onSubmit={handleSubmit}
        >
            <div key={key}>
                <div className={styles.search}>
                    <Field
                        field={{
                            name: 'search',
                            type: FieldType.TEXT,
                            placeholder: 'Buscar por nombre o código',
                            icon: 'search'
                        }}
                        onChange={(value?: FieldValue) => setSearchText(value as O<string>)}
                    />
                </div>
                {visibleProducts.length === 0 ? (
                    <p className={styles.empty}>No hay productos para habilitar.</p>
                ) : (
                    <>
                        <div className={styles.selectAll}>
                            <BooleanField
                                id="negative-stock-select-all"
                                label={`Seleccionar todos los visibles (${selectableVisibleIds.length})`}
                                value={allVisibleSelected}
                                optional
                                disabled={selectableVisibleIds.length === 0}
                                onChange={onToggleAllVisible}
                            />
                        </div>
                        <div className={styles.list}>
                            {visibleProducts.map(product => {
                                const status = getStatus(product)
                                const reason = disabledReason(product)
                                return (
                                    <div key={product.id} className={statusClassName[status]}>
                                        <BooleanField
                                            id={`negative-stock-product-${product.id}`}
                                            label={`#${product.code} - ${product.name}`}
                                            value={status === ProductStatus.BY_CATEGORY || selectedIds.includes(product.id)}
                                            optional
                                            disabled={!!reason}
                                            onChange={onToggleProduct(product.id)}
                                        />
                                        {reason && <span className={styles.disabledReason}>({reason})</span>}
                                    </div>
                                )
                            })}
                        </div>
                    </>
                )}
            </div>
        </Modal>
    )
}
